import {t} from 'i18next';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import {Box, Grid, InputAdornment, Typography} from '@mui/material';
import ArrTreeView from '../../../common/arr-tree-view/ArrTreeView';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Search} from '@mui/icons-material';
import {transformJsTreeApiToTreeData} from '../../actionMenuUtils';
import {useGetBulkTagList} from '../../../../hooks/assignments/useAssignments';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ExtendedTreeItemProps} from '../../../../shared/interfaces';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import Clear from '@mui/icons-material/CancelOutlined';

interface IExistingTagProps {
  selectedExistingTags: string[];
  setSelectedExistingTags: (value: string[] | null | undefined) => void;
  searchInputExistingTags: string;
  setSearchInputExistingTags: (value: string) => void;
  setTagTreeData?: (data: TreeViewBaseItem<ExtendedTreeItemProps>[]) => void;
}

// Helper function to get expanded ids based on the search keyword
const getExpandedIdsForSearch = (
  nodes: TreeViewBaseItem<ExtendedTreeItemProps>[],
  keyword: string
): string[] => {
  const ids = new Set<string>();
  const text = keyword.toLowerCase();

  const dfs = (node: TreeViewBaseItem<ExtendedTreeItemProps>) => {
    const isMatch = node.label?.toLowerCase().includes(text);
    let childMatch = false;

    if (node.children?.length) {
      for (const child of node.children) {
        if (dfs(child)) childMatch = true;
      }
    }

    if (isMatch || childMatch) {
      ids.add(node.id);
      return true;
    }

    return false;
  };

  nodes.forEach((root) => dfs(root));
  return Array.from(ids);
};

const ExistingTag: React.FC<IExistingTagProps> = ({
  selectedExistingTags,
  setSelectedExistingTags,
  searchInputExistingTags,
  setSearchInputExistingTags,
  setTagTreeData,
}) => {
  const enBulkTag = 'actionMenu.bulkTagFlyout';
  const [searchInput, setSearchInput] = useState<string>(
    searchInputExistingTags
  );
  const [treeKey, setTreeKey] = useState<number>(0);
  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const {data: BulkTagData, isPending: isLoading} =
    useGetBulkTagList('BulkTag');
  const treeData = useMemo(() => {
    return transformJsTreeApiToTreeData(BulkTagData);
  }, [BulkTagData]);

  useEffect(() => {
    if (!treeData || treeData.length === 0) return;

    if (!searchInputExistingTags) {
      // ON INITIAL LOAD / RELOAD: Only map the IDs of the top-level nodes
      const firstLayerIds = treeData.map((node) => node.id);
      setExpandedIds(firstLayerIds);
    } else {
      // ON SEARCH: Expand matching branches
      const expanded = getExpandedIdsForSearch(
        treeData,
        searchInputExistingTags
      );
      setExpandedIds(expanded);
    }

    // Force remount to apply the expansion state
    setTreeKey((prev) => prev + 1);
  }, [treeData, searchInputExistingTags]);

  useEffect(() => {
    const expanded = getExpandedIdsForSearch(treeData, searchInputExistingTags);
    setExpandedIds(expanded);
    setTreeKey((prev) => prev + 1); // force remount to apply expandedIds
  }, [searchInputExistingTags, treeData]);

  useEffect(() => {
    if (treeData?.length && setTagTreeData) {
      setTagTreeData(treeData);
    }
  }, [treeData, setTagTreeData]);

  const handleSearchKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const searchText = searchInput.trim();
      setSearchInputExistingTags(searchText);
    }
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchInputExistingTags('');
  };

  const handleSelected = useCallback(
    (values: string[] | null) => {
      // Delay state update so it happens AFTER render, avoiding the error
      setTimeout(() => {
        setSelectedExistingTags(values);
      }, 0);
    },
    [setSelectedExistingTags]
  );

  return (
    <>
      <Box className="search-box">
        <Grid container columnSpacing={2}>
          <Grid size={{xs: 12, sm: 6}}>
            <ArrTextField
              state="enabled"
              sizeVariant="medium"
              className="search-input"
              placeholder={t(`${enBulkTag}.searchPlaceholder`)}
              aria-label={t(`${enBulkTag}.searchPlaceholder`)}
              dataTestId="existing-tag-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyEnter}
              slotProps={{
                inputLabel: {shrink: false},
                input: {
                  startAdornment: (
                    <InputAdornment
                      position="end"
                      className="search-adornment-icon"
                    >
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput ? (
                    <InputAdornment position="end">
                      <Clear
                        onClick={handleClearSearch}
                        style={{cursor: 'pointer'}}
                        data-testid="search-clear-btn"
                        tabIndex={0}
                      />
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          </Grid>
        </Grid>
      </Box>
      <Box className="tree-heading-box">
        <Typography
          component="h4"
          role="heading"
          className="typo-text subhead4_size"
          data-testid="select-existing-tags"
          tabIndex={0}
        >
          {t(`${enBulkTag}.selectExistingTags`)}
        </Typography>
      </Box>

      <Box
        className="tag-tree-box existing-tree"
        data-testid="existing-tag-tree"
      >
        {isLoading || !treeData.length ? (
          <ArrLoadingSpinner />
        ) : (
          <ArrTreeView
            key={treeKey}
            itemsList={treeData}
            selectedItem={''}
            handleNoResultMessage={t(`${enBulkTag}.noResult`)}
            allowGlobalSelect={true}
            treeSelectedItems={selectedExistingTags}
            multipleSelection={true}
            isShowCountLabel={false}
            isShowCheckbox={true}
            showActions={false}
            customContentHeight={36}
            defaultExpandedIds={selectedExistingTags}
            setSelectedItem={handleSelected}
            searchKeyword={searchInputExistingTags}
            setExpandedFolders={setExpandedIds}
          />
        )}
      </Box>
    </>
  );
};

export default ExistingTag;
